import { type ApolloQueryResult, useApolloClient } from '@apollo/client';
import { type ModalProps } from 'antd';
import { useMemo, useState, useCallback, useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AddTradeInAuditTrailCommentDocument,
    type AddTradeInAuditTrailCommentMutation,
    type AddTradeInAuditTrailCommentMutationVariables,
} from '../../../../api/mutations/addTradeInAuditTrailComment';
import {
    type GetTradeInAuditTrailsQuery,
    type GetTradeInAuditTrailsQueryVariables,
    useGetTradeInAuditTrailsQuery,
} from '../../../../api/queries/getTradeInAuditTrails';
import { pageReducer } from '../../../../components/PaginatedTable';
import InternalErrorResult from '../../../../components/results/InternalErrorResult';
import ActivityLogTable from '../ActivityLogTable';
import CommentModal, { type CommentFormValues } from './CommentModal';
import { StyledContainer, StyledLink, StyledTitle } from './shared';

const useCommentModal = (
    tradeInId: string,
    refetch: (
        variables?: Partial<GetTradeInAuditTrailsQueryVariables>
    ) => Promise<ApolloQueryResult<GetTradeInAuditTrailsQuery>>
) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const apolloClient = useApolloClient();

    const onSubmit = useCallback(
        async (values: CommentFormValues) => {
            await apolloClient.mutate<
                AddTradeInAuditTrailCommentMutation,
                AddTradeInAuditTrailCommentMutationVariables
            >({
                mutation: AddTradeInAuditTrailCommentDocument,
                variables: {
                    tradeInId,
                    comment: values.comment,
                },
            });

            refetch();
        },
        [apolloClient, tradeInId, refetch]
    );

    const actions = useMemo(
        () => ({
            open: () => setIsModalOpen(true),
            close: () => setIsModalOpen(false),
        }),
        [setIsModalOpen]
    );

    return {
        ...actions,
        render: (props: ModalProps) => (
            <CommentModal {...props} isModalOpen={isModalOpen} onClose={actions.close} onSubmit={onSubmit} />
        ),
    };
};

type TradeInActivityLogWithCommentProps = {
    tradeInId: string;
    timezone: string;
};

const TradeInActivityLogWithComment = ({ tradeInId, timezone }: TradeInActivityLogWithCommentProps) => {
    const { t } = useTranslation(['auditTrails', 'common']);

    const [state, dispatch] = useReducer(pageReducer, { page: 1, pageSize: 10 });
    const { page, pageSize } = state;

    const ModalTitle = <StyledTitle>{t('auditTrails:fields.comment.label')}</StyledTitle>;
    const okText = <div>{t('common:actions.save')}</div>;

    const { data, loading, error, refetch } = useGetTradeInAuditTrailsQuery({
        variables: { tradeInId, pagination: { limit: pageSize, offset: (page - 1) * pageSize } },
    });

    // resolve activity log not refresh issue when go in detail
    useEffect(() => {
        refetch();
    }, []);

    const modal = useCommentModal(tradeInId, refetch);

    if (!loading && error) {
        return <InternalErrorResult />;
    }

    return (
        <>
            <StyledContainer>
                <h1>{t('auditTrails:title')}</h1>
                <StyledLink onClick={modal.open}>{t('auditTrails:addComment')}</StyledLink>
            </StyledContainer>
            <ActivityLogTable
                count={data?.result?.count ?? 0}
                data={data?.result?.items ?? []}
                dispatch={dispatch}
                state={state}
                timezone={timezone}
            />
            {modal.render({
                okText,
                title: ModalTitle,
            })}
        </>
    );
};

export default TradeInActivityLogWithComment;
